import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Theme Context - Issue #98: Dark Mode Implementation
 * Manages global theme state with localStorage persistence
 *
 * Themes: 'light' (default), 'dark'
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = 'universe_theme';

/**
 * Theme Provider - wrap your app with this component
 * Automatically applies theme to document body via data-theme attribute
 */
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to 'light'
  const [theme, setThemeState] = useState(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    return savedTheme === 'dark' ? 'dark' : 'light';
  });

  // Apply theme to document body whenever it changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Wrapper function to set theme
  const setTheme = (newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
    }
  };

  // Toggle between light and dark
  const toggleTheme = () => {
    setThemeState(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook to access theme state and functions
 * Usage: const { theme, setTheme, toggleTheme } = useTheme();
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
